import subDays from "date-fns/subDays";
import { sumPortfolioBuckets } from "./groupPortfolioBucketsForSparkline";

export const getBreakdownBatchSize = (breakdown) => {
  if (breakdown === "weekly") {
    return 7;
  }
  if (breakdown === "monthly") {
    return 28;
  }
  return 1;
};

/**
 * Batches ordered indices from the end of the series (parity with AdsCampaignStatistics).
 */
export const batchIndicesFromEnd = (length, batchSize) => {
  if (!length || batchSize <= 1) {
    return Array.from({ length }, (_, index) => [index]);
  }

  const batches = [];
  let remaining = Array.from({ length }, (_, index) => index);

  while (remaining.length > 0) {
    if (remaining.length >= batchSize) {
      const batch = remaining.slice(-batchSize);
      remaining = remaining.slice(0, -batchSize);
      batches.unshift(batch);
    } else {
      batches.unshift(remaining);
      remaining = [];
    }
  }

  return batches;
};

/**
 * Groups daily portfolio buckets for time-series charts (daily / weekly / monthly).
 */
export const groupPortfolioTimeSeriesByBreakdown = ({
  portfolioBuckets = [],
  buckets = [],
  breakdown = "daily",
}) => {
  const batchSize = getBreakdownBatchSize(breakdown);

  if (!portfolioBuckets.length || batchSize === 1) {
    return { portfolioBuckets, buckets };
  }

  const indexBatches = batchIndicesFromEnd(portfolioBuckets.length, batchSize);
  const groupedPortfolioBuckets = [];
  const groupedBuckets = [];

  indexBatches.forEach((indices) => {
    const slice = indices.map((index) => portfolioBuckets[index]);
    groupedPortfolioBuckets.push(sumPortfolioBuckets(slice));

    const firstMeta = buckets[indices[0]] || {};
    const lastMeta = buckets[indices[indices.length - 1]] || {};
    let startDateTime = firstMeta.startDateTime;
    const endDateTime = lastMeta.endDateTime;

    if (indices.length < batchSize && endDateTime) {
      const endDate = new Date(endDateTime);
      startDateTime = subDays(endDate, batchSize - 1).getTime();
    }

    groupedBuckets.push({ startDateTime, endDateTime });
  });

  return {
    portfolioBuckets: groupedPortfolioBuckets,
    buckets: groupedBuckets,
  };
};

/**
 * Groups goals historical rows (sum delta per period) for projection chart display.
 */
export const groupGoalsHistoricalRowsByBreakdown = ({
  historicalRows = [],
  breakdown = "daily",
}) => {
  const batchSize = getBreakdownBatchSize(breakdown);

  if (!historicalRows.length || batchSize === 1) {
    return historicalRows;
  }

  const indexBatches = batchIndicesFromEnd(historicalRows.length, batchSize);

  return indexBatches.map((indices) => {
    const slice = indices.map((index) => historicalRows[index]);
    const firstRow = slice[0];
    const lastRow = slice[slice.length - 1];

    let startDateTime = firstRow.startDateTime;
    const endDateTime = lastRow.endDateTime;
    let date = firstRow.date;

    if (indices.length < batchSize && endDateTime) {
      const endDate = new Date(endDateTime);
      date = subDays(endDate, batchSize - 1);
      startDateTime = date.getTime();
    }

    const delta = slice.reduce((sum, row) => sum + Number(row.delta || 0), 0);

    return {
      startDateTime,
      endDateTime,
      date,
      delta,
    };
  });
};

/**
 * Groups forward daily forecast values (sum per period) for chart display.
 */
export const groupForecastDailyValuesByBreakdown = ({
  forecastDailyResults = [],
  breakdown = "daily",
}) => {
  const batchSize = getBreakdownBatchSize(breakdown);
  const values = Array.isArray(forecastDailyResults)
    ? forecastDailyResults.map((value) => Number(value) || 0)
    : [];

  if (!values.length || batchSize === 1) {
    return values.map((value, index) => ({
      value,
      startDayOffset: index + 1,
      endDayOffset: index + 1,
      daysInBatch: 1,
    }));
  }

  const grouped = [];

  for (let i = 0; i < values.length; i += batchSize) {
    const slice = values.slice(i, i + batchSize);
    grouped.push({
      value: slice.reduce((sum, v) => sum + v, 0),
      startDayOffset: i + 1,
      endDayOffset: i + slice.length,
      daysInBatch: slice.length,
    });
  }

  return grouped;
};

export default groupPortfolioTimeSeriesByBreakdown;
